/**
 * How an order and its client are LABELLED on the screens that show a confirmed
 * order — Confirmed Order list and picker, Vendor Purchase Order list, Vendor
 * Invoice assign-order, Client Invoice page, Client Invoices create picker. A confirmed order is named by its QUO number
 * (owner decision 2026-10-02; see `orderRef`).
 *
 * Deborah, 2026-09-04, on the Confirmed Order list: the Client Name column was
 * printing a raw email From header (`"Ayushi Soni" <a...`) and the `#` column
 * was printing the row's position on the page, which changes when you sort or
 * page and is not an order number by any reading. Both were per-page code —
 * `QuotationTable` had a `parseCustomerName` that got the name right, and the
 * page beside it had none — so the same order read three different ways
 * depending on which screen you were on.
 *
 * Client-only, and deliberately NOT a mirrored pair (so not in
 * `mirroredUtilParity.test.js`): the server never has to turn an RFC-5322 From
 * header into something a person reads. `chainRef.ts` is the precedent.
 */

/** An order as any of the three lists knows it. Only the label fields. */
export interface OrderRefFields {
  rfqNumber?: string | null;
  quotationNumber?: string | null;
}

/**
 * `"Name" <email@host>` → `Name`. A bare address stays the address.
 *
 * Returning the ADDRESS rather than inventing a name from it is the honest
 * fallback: `invoice+statements@render.com` is what we actually know about that
 * client, and a prettified "Invoice Statements" would be a fact nobody stated.
 * A caller renders `''` however it renders "unknown" — the two lists spell that
 * differently ('N/A' and '--') and neither spelling belongs in here.
 */
export function clientDisplayName(raw?: string | null): string {
  // A name saved before F21 (2026-10-01) went through the old global input escaper, which wrote the two
  // angle brackets of `"Name" <email>` as &lt; and &gt; (not the quotes, not the ampersand). Read as typed:
  // React escapes once, on output, so a stored entity otherwise prints as the literal text "&lt;" (E3a).
  const text = String(raw ?? '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
  if (!text) return '';

  // `"Name" <email>` or `Name <email>`.
  const withAngle = text.match(/^"?([^"<]*)"?\s*<([^>]+)>$/);
  if (withAngle) {
    const name = withAngle[1].trim();
    const email = withAngle[2].trim();
    return name || email;
  }

  return text;
}

/**
 * The number that identifies a confirmed order: its QUOTATION number (QUO-…),
 * falling back to the RFQ number only for an order that has none.
 *
 * Owner decision 2026-10-02: a confirmed order is named by its QUO number on
 * EVERY screen, because the detail page titles itself by `quotationNumber` and
 * a list that printed the RFQ beside it made one order read as two. (This used
 * to be RFQ-first; one list was patched to QUO by reading the field directly,
 * which split the screens — the fix is here, in the owner.) Callers label a
 * CONFIRMED order only: Confirmed Order list, its picker, Vendor PO list, the
 * Vendor Invoice assign-order label, the Client Invoices create-from-order
 * picker (`formatConfirmedOrderLabel`), and the Client Invoice page's "Confirmed
 * order" line and export. There is no `orderNumber` field.
 *
 * Returns null, never `''` or `'undefined'`, so the caller has to decide what
 * an unnumbered order looks like.
 */
export function orderRef(order: OrderRefFields | null | undefined): string | null {
  if (!order) return null;
  const quotation = String(order.quotationNumber ?? '').trim();
  if (quotation) return quotation;
  const rfq = String(order.rfqNumber ?? '').trim();
  if (rfq) return rfq;
  return null;
}
